import { useState, useEffect } from "react"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { Eye, EyeOff, AlertCircle, Loader } from "lucide-react"
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth"
import { doc, setDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../services/firebase"
import InvitationService from "../services/InvitationService"
import { useAuth } from "../contexts/AuthProvider"
import { InvitationUtils } from "../utils/invitationUtils"
import { RedirectUtils } from "../utils/redirectUtils"
import { useMixpanel } from "../hooks/useMixpanel"
import { sendEmail } from "../services/emailService"
import logo from "../assets/logo_h.svg"

const CreatePasswordPage = () => {
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [email, setEmail] = useState("")
  const [userName, setUserName] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [invitation, setInvitation] = useState(null)

  const location = useLocation()
  const navigate = useNavigate()
  const { refreshAuth } = useAuth()
  const { track } = useMixpanel()

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email)
      if (location.state.invitation) {
        setInvitation(location.state.invitation)
      }
    } else {
      navigate("/register", { replace: true })
    }
  }, [location, navigate])

  // Candidate doc ID = base64 of email
  const getDocIdFromEmail = (email) => btoa(email.toLowerCase().trim())

  const sendWelcomeEmail = (name, email) => {
    sendEmail({
      email,
      subject: "Welcome to Resolut",
      text: `Hi ${name},\n\nWelcome to the Resolut Club — we're excited to have you on board!\n\nComplete your profile at https://resolut.co.in/profile\n\nBest,\nPatrick\nCo-founder, Resolut`,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")

    if (!userName.trim()) {
      setError("Please enter your name")
      return
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long")
      return
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match")
      return
    }

    setLoading(true)
    try {
      const auth = getAuth()
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      const user = userCredential.user

      const docId = getDocIdFromEmail(email)
      await setDoc(doc(db, "users", docId), {
        uid: user.uid,
        email,
        name: userName,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        accountType: "candidate",
      })

      // Handle invitation
      const storedInvitation = InvitationUtils.getStoredInvitation()
      if (storedInvitation) {
        try {
          await InvitationService.acceptInvitation(storedInvitation.id, user.uid)
          InvitationUtils.clearInvitation()
        } catch (invitationError) {
          console.error("Error processing invitation:", invitationError)
        }
      }

      if (refreshAuth) await refreshAuth()
      await new Promise((r) => setTimeout(r, 500))

      sendWelcomeEmail(userName, email)
      track("Account Created", { name: userName, type: "candidate" })

      RedirectUtils.redirectToStoredUrl(navigate, "/profile")
    } catch (err) {
      console.error("Registration error:", err)
      const errorMessages = {
        "auth/email-already-in-use": "An account already exists with this email",
        "auth/invalid-email": "Invalid email address format",
        "auth/weak-password": "Password is too weak. Please use at least 6 characters",
      }
      setError(errorMessages[err.code] || err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white">
      <div className="w-full max-w-md px-6">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <div className="flex items-center justify-center mb-4">
            <img src={logo} alt="Resolut Logo" className="w-6 h-6 drop-shadow-md mr-2" />
            <h1 className="text-2xl font-bold text-gray-800">Resolut</h1>
          </div>
          <h2 className="text-3xl font-medium text-center">Create your account</h2>
        </div>

        {/* Email pill */}
        <div className="mb-6 flex items-center justify-between py-2 px-4 bg-gray-100 rounded-full">
          <span className="text-gray-900 text-sm truncate">{email}</span>
          <button onClick={() => navigate("/register")} className="text-blue-600 text-sm font-medium ml-2">
            Edit
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 text-red-700 rounded-md text-sm flex items-center">
            <AlertCircle size={16} className="mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-1">Your name</label>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-gray-900 placeholder-gray-400"
              required
              placeholder="Your full name"
            />
          </div>

          {/* Password */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-gray-900 placeholder-gray-400"
                required
                placeholder="At least 6 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-600 mb-1">Confirm password</label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-gray-900 placeholder-gray-400"
              required
              placeholder="Confirm your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-white font-medium ${
              loading ? "bg-gray-500" : "bg-gray-900 hover:bg-gray-700"
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <Loader size={16} className="animate-spin mr-2" />
                Creating account...
              </span>
            ) : (
              "Create account"
            )}
          </button>
        </form>

        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Already have an account?{" "}
            <button
              onClick={() => navigate("/login", { state: { returnUrl: location.state?.returnUrl } })}
              className="text-gray-900 font-medium underline hover:text-gray-600 bg-transparent border-none cursor-pointer"
            >
              Log in
            </button>
          </p>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-xs text-gray-400">
          <div className="flex justify-center space-x-4">
            <a href="/terms" className="hover:text-gray-600">Terms of Use</a>
            <span>|</span>
            <a href="/privacy" className="hover:text-gray-600">Privacy Policy</a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreatePasswordPage